import { InstallsSplitRow } from '../../types/marketing';
import { formatDay, formatInt, niceMax } from './marketingMath';

const W = 720; const H = 220;
const PLOT = { left: 44, right: W - 16, top: 12, bottom: H - 34 };

/** Spec 084 Channels tab — store installs per day, stacked: attributed to a campaign (bottom) and other (top). */
export default function InstallsSplitChart({ rows }: { rows: InstallsSplitRow[] }) {
  const n = Math.max(rows.length, 1);
  const slot = (PLOT.right - PLOT.left) / n;
  const bw = Math.min(slot * 0.68, 36);
  const max = Math.max(2, niceMax(Math.max(0, ...rows.map((r) => r.attributed + r.other))));
  const h = PLOT.bottom - PLOT.top;
  const every = Math.max(1, Math.ceil(rows.length / 8));
  const total = (k: 'attributed' | 'other') => rows.reduce((a, r) => a + r[k], 0);
  const summary = rows.length === 0 ? 'No days in this range.'
    : `Installs per day, ${formatDay(rows[0].date)} to ${formatDay(rows[rows.length - 1].date)}: ${formatInt(total('attributed'))} attributed to a campaign, ${formatInt(total('other'))} other.`;
  return (
    <figure className="mkt-chart" data-testid="installs-split-chart">
      <figcaption className="mkt-chart__head">
        <span className="mkt-chart__title">Installs per day</span>
        <span className="mkt-legend" aria-hidden="true">
          <span className="mkt-legend__item"><i className="mkt-swatch mkt-swatch--app" />Attributed to a campaign</span>
          <span className="mkt-legend__item"><i className="mkt-swatch mkt-swatch--other" />Other</span>
        </span>
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={summary} className="mkt-chart__svg">
        {[0, 0.5, 1].map((t) => {
          const y = PLOT.bottom - t * h;
          return (
            <g key={t} aria-hidden="true">
              <line className="mkt-grid" x1={PLOT.left} x2={PLOT.right} y1={y} y2={y} />
              <text className="mkt-axis" x={PLOT.left - 8} y={y + 4} textAnchor="end">{formatInt(Math.round(max * t))}</text>
            </g>
          );
        })}
        {rows.map((r, i) => {
          const cx = PLOT.left + slot * (i + 0.5);
          const aH = (r.attributed / max) * h;
          const oH = (r.other / max) * h;
          return (
            <g key={r.date} data-day={r.date}>
              <title>{`${formatDay(r.date)}: ${r.attributed} attributed, ${r.other} other`}</title>
              <rect className="mkt-bar mkt-bar--app" x={cx - bw / 2} y={PLOT.bottom - aH} width={bw} height={aH} />
              <rect className="mkt-bar mkt-bar--other" x={cx - bw / 2} y={PLOT.bottom - aH - oH} width={bw} height={oH} />
              {i % every === 0 && <text className="mkt-axis" x={cx} y={PLOT.bottom + 18} textAnchor="middle" aria-hidden="true">{formatDay(r.date)}</text>}
            </g>
          );
        })}
      </svg>
      <table className="sr-only">
        <caption>{summary}</caption>
        <thead><tr><th>Day</th><th>Attributed</th><th>Other</th></tr></thead>
        <tbody>{rows.map((r) => <tr key={r.date}><td>{formatDay(r.date)}</td><td>{r.attributed}</td><td>{r.other}</td></tr>)}</tbody>
      </table>
    </figure>
  );
}
